import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout, { PageHeader } from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";
import useLiveSync from "../lib/useLiveSync";
import { useAuth } from "../context/AuthContext";
import { InventoryLogTable } from "../components/InventoryLogTable";
import { AuditLogTable } from "../components/AuditLogTable";
import { KitchenDayLog } from "../components/KitchenDayLog";
import { IssuesTable } from "../components/IssuesTable";
import {
  CheckCheck, BarChart3, AlertTriangle, Package, ShoppingBag, Bell, ChevronRight,
} from "lucide-react";

const TYPE_LABEL = {
  low_stock: "Low Stock",
  daily_report: "Daily Report",
  packaging_request: "Packaging",
  packaging_approved: "Packaging Approved",
  packaging_rejected: "Packaging Rejected",
  packaging_fulfilled: "Packaging Fulfilled",
  order: "Order",
  production: "Daily Production",
};

const TYPE_ICONS = {
  low_stock: AlertTriangle,
  daily_report: BarChart3,
  packaging_request: Package,
  packaging_approved: Package,
  packaging_rejected: Package,
  packaging_fulfilled: Package,
  order: ShoppingBag,
};

const SEVERITY_TONES = {
  high:   "bg-destructive/10 text-destructive ring-destructive/20",
  medium: "bg-[hsl(var(--accent-gold-soft))] text-[hsl(var(--primary))] ring-[hsl(var(--primary))]/15",
  low:    "bg-secondary text-muted-foreground ring-border",
};

function relativeTime(iso) {
  const d = new Date(iso); const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function groupByDay(items) {
  const groups = {};
  for (const n of items) {
    const d = new Date(n.created_at);
    const key = d.toDateString();
    (groups[key] = groups[key] || []).push(n);
  }
  return Object.entries(groups).map(([day, list]) => ({
    day,
    label: friendlyDay(day),
    items: list,
  }));
}

function friendlyDay(s) {
  const d = new Date(s); const now = new Date();
  const today = now.toDateString();
  const yest = new Date(now.getTime() - 86400000).toDateString();
  if (s === today) return "Today";
  if (s === yest) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
}

export default function Activity() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "feed";
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState({ unread: 0, notifications: [] });
  const [reports, setReports] = useState([]);
  const [reportsLoaded, setReportsLoaded] = useState(false);
  const [feedLoaded, setFeedLoaded] = useState(false);

  const load = () => api.get(`/notifications?limit=200${isAdmin ? "&scope=all" : ""}`).then((r) => setData(r.data)).finally(() => setFeedLoaded(true));
  const loadReports = () =>
    api.get("/commissary/daily-reports").then((r) => setReports(r.data)).catch(() => {}).finally(() => setReportsLoaded(true));

  useLiveSync(() => { load(); loadReports(); }, { interval: 10000 });

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    toast.success("All notifications marked read");
    load();
  };
  const markRead = async (n) => {
    if (n.read) return;
    await api.post(`/notifications/${n.id}/read`); load();
  };

  const grouped = groupByDay(data.notifications);

  return (
    <Layout>
      <PageHeader
        eyebrow="Activity Center"
        title="Activity"
        description={isAdmin
          ? "Live Feed refreshes every 10 seconds and shows every event from every portal: orders placed and each status change, Kitchen daily production, franchisee daily reports, invoices drafted, sent and paid, packaging requests, and low-stock alerts. Inventory Log lists every stock change; Audit Log records every action and sign-in."
          : "Live Feed refreshes every 10 seconds: your orders and their status changes, Kitchen daily production, daily reports, invoices and low-stock alerts that concern you."}
        actions={data.unread > 0 && tab === "feed" && (
          <button onClick={markAllRead} data-testid="mark-all-read-btn"
            className="h-11 px-5 rounded-xl border border-border bg-card text-sm font-medium hover:bg-secondary transition-all inline-flex items-center gap-2">
            <CheckCheck className="h-4 w-4" /> Mark all read ({data.unread})
          </button>
        )}
      />

      <div className="flex gap-2 mb-6 flex-wrap" data-testid="activity-tabs">
        {[
          { k: "feed", label: `Live Feed`, count: data.unread },
          { k: "daily-reports", label: "Daily Reports" },
          ...(isAdmin || user?.role === "employee" || user?.warehouse ? [{ k: "day-log", label: "Daily Kitchen Log" }] : []),
          { k: "inventory-log", label: isAdmin ? "Inventory Log" : "Stock Changes" },
          ...(isAdmin ? [{ k: "audit-log", label: "Audit Log" }, { k: "issues", label: "Issues" }] : []),
        ].map((t) => (
          <button key={t.k} onClick={() => setParams({ tab: t.k })}
            data-testid={`activity-tab-${t.k}`}
            className={`h-11 px-5 rounded-full text-sm font-medium transition-all inline-flex items-center gap-2 ${
              tab === t.k ? "bg-foreground text-background" : "bg-card border border-border text-muted-foreground hover:text-foreground"
            }`}>
            {t.label}
            {t.count > 0 && (
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${tab === t.k ? "bg-primary text-primary-foreground" : "bg-primary/15 text-primary"}`}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "feed" && (
        <div data-testid="notifications-feed" className="space-y-8">
          {!feedLoaded ? (
            <div className="luxury-card p-12">
              <div className="space-y-3">{[1,2,3,4].map(k => <div key={k} className="h-12 rounded shimmer" />)}</div>
            </div>
          ) : grouped.length === 0 ? (
            <div className="luxury-card p-20 text-center">
              <Bell className="h-7 w-7 mx-auto mb-3 text-muted-foreground/40" />
              <p className="font-medium text-sm">All clear</p>
              <p className="text-xs text-muted-foreground mt-1">No operational events to surface right now.</p>
            </div>
          ) : grouped.map((g) => (
            <section key={g.day}>
              <div className="label-eyebrow mb-3 px-1">{g.label}</div>
              <ul className="luxury-card divide-y divide-border overflow-hidden">
                {g.items.map((n) => {
                  const Icon = TYPE_ICONS[n.type] || Bell;
                  const inner = (
                    <div className="flex items-start gap-4">
                      <div className={`h-10 w-10 rounded-full ring-1 flex items-center justify-center shrink-0 ${SEVERITY_TONES[n.severity] || SEVERITY_TONES.low} ${n.read ? "opacity-60" : ""}`}>
                        <Icon className="h-4 w-4" strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <div className={`${n.read ? "text-muted-foreground" : "font-medium"} text-sm`}>{n.title}</div>
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground bg-secondary px-2 py-0.5 rounded-full">
                            {TYPE_LABEL[n.type] || n.type}
                          </span>
                          {!n.read && (
                            <span className="inline-flex items-center gap-1 text-[10px] text-primary font-semibold">
                              <span className="h-1.5 w-1.5 rounded-full bg-primary pulse-dot" /> NEW
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">{n.message}</div>
                        <div className="text-[10px] text-muted-foreground mt-1.5 inline-flex items-center gap-2">
                          <span>{relativeTime(n.created_at)}</span>
                          <span className="text-muted-foreground/50">·</span>
                          <span>{new Date(n.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                        </div>
                      </div>
                      {n.link && <ChevronRight className="h-4 w-4 text-muted-foreground/50 shrink-0 mt-1" />}
                    </div>
                  );
                  return (
                    <li key={n.id} onClick={() => markRead(n)} data-testid={`feed-item-${n.id}`}
                        className={`px-5 py-4 cursor-pointer hover:bg-secondary/30 transition-colors ${!n.read ? "bg-secondary/15" : ""}`}>
                      {n.link ? <Link to={n.link} className="block">{inner}</Link> : inner}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}

      {tab === "inventory-log" && <InventoryLogTable />}
      {tab === "issues" && isAdmin && <IssuesTable />}
      {tab === "audit-log" && isAdmin && <AuditLogTable />}
      {tab === "day-log" && (isAdmin || user?.role === "employee" || user?.warehouse) && <KitchenDayLog />}

      {tab === "daily-reports" && (
        <div className="luxury-card overflow-hidden">
          <table className="w-full text-sm" data-testid="daily-reports-table">
            <thead className="bg-secondary/60">
              <tr className="text-left">
                <th className="px-5 py-3 label-eyebrow font-medium">Report</th>
                <th className="px-5 py-3 label-eyebrow font-medium">Date</th>
                <th className="px-5 py-3 label-eyebrow font-medium">By</th>
                <th className="px-5 py-3 label-eyebrow font-medium text-right">Items</th>
                <th className="px-5 py-3 label-eyebrow font-medium text-right">Adjustments</th>
                <th className="px-5 py-3 label-eyebrow font-medium text-right">Low</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-secondary/30 transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{r.report_no}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-muted-foreground">{r.date}</td>
                  <td className="px-5 py-3.5 text-muted-foreground">{r.submitted_by_name}</td>
                  <td className="px-5 py-3.5 text-right tabular-nums">{r.item_count}</td>
                  <td className="px-5 py-3.5 text-right tabular-nums">{r.adjustment_count}</td>
                  <td className="px-5 py-3.5 text-right">
                    {r.low_stock_count > 0
                      ? <span className="text-destructive font-medium tabular-nums">{r.low_stock_count}</span>
                      : <span className="text-muted-foreground tabular-nums">0</span>}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <Link to={`/activity/reports/${r.id}`} data-testid={`view-report-${r.id}`}
                      className="text-sm font-medium hover:text-primary inline-flex items-center gap-1">
                      View <ChevronRight className="h-3.5 w-3.5" />
                    </Link>
                  </td>
                </tr>
              ))}
              {reports.length === 0 && (
                <tr><td colSpan="7" className="px-5 py-16 text-center">
                  {reportsLoaded ? (
                    <>
                      <BarChart3 className="h-6 w-6 mx-auto mb-3 text-muted-foreground/40" />
                      <p className="font-medium text-sm">No reports yet</p>
                      <p className="text-xs text-muted-foreground mt-1">Submit a daily inventory from Kitchen to lock the first record.</p>
                    </>
                  ) : (
                    <p className="text-muted-foreground text-sm">Loading…</p>
                  )}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
}
